import { PlayerProperty, Save, Restore } from '../PlayerProperty';
import IPlayerData from '../../IPlayerData';
import { IPlayerState } from '../../../State/Player/IPlayerState';
import { restoreDate, restoreStringifiedArray } from '../../ParseHelper';
import { IFriendData } from './IPlayerSocialData';

type PlayerStateKey = 'social';
type PlayerDataKeys =
    | 'GBCUserId'
    | 'receivedMessages'
    | 'friendGifts'
    | 'friendAltGifts'
    | 'friendCurrencyRequests'
    | 'friendsSentCurrency'
    | 'friendHelper';
export default class Social implements PlayerProperty<PlayerStateKey, PlayerDataKeys> {
    public isChanged(playerStateData: IPlayerState, playerEndpointData: IPlayerData): boolean {
        const friendData: IFriendData[] = playerStateData.social.friendData;
        return (
            JSON.stringify(playerEndpointData.GBCUserId) !== JSON.stringify(playerStateData.social.userId) ||
            playerEndpointData.receivedMessages !== JSON.stringify(playerStateData.social.receivedMessages) ||
            playerEndpointData.friendGifts !== JSON.stringify(this.getGiftArray(friendData)) ||
            playerEndpointData.friendAltGifts !== JSON.stringify(this.getAltGiftsArray(friendData)) ||
            playerEndpointData.friendCurrencyRequests !== JSON.stringify(this.getFriendRequestArray(friendData)) ||
            playerEndpointData.friendsSentCurrency !== JSON.stringify(this.getFriendSendArray(friendData)) ||
            playerEndpointData.friendHelper !== JSON.stringify(this.getFriendHelperArray(friendData))
        );
    }

    public restore(playerEndpointData: IPlayerData): Restore<PlayerStateKey> {
        return {
            userId: playerEndpointData.GBCUserId,
            receivedMessages: restoreStringifiedArray(playerEndpointData.receivedMessages, []),
            friendData: this.mergeFriendData(playerEndpointData),
        };
    }

    public save(playerStateData: IPlayerState): Save<PlayerDataKeys> {
        return {
            GBCUserId: playerStateData.social.userId,
            receivedMessages: JSON.stringify(playerStateData.social.receivedMessages),
            friendGifts: JSON.stringify(this.getGiftArray(playerStateData.social.friendData)),
            friendAltGifts: JSON.stringify(this.getAltGiftsArray(playerStateData.social.friendData)),
            friendCurrencyRequests: JSON.stringify(this.getFriendRequestArray(playerStateData.social.friendData)),
            friendsSentCurrency: JSON.stringify(this.getFriendSendArray(playerStateData.social.friendData)),
            friendHelper: JSON.stringify(this.getFriendHelperArray(playerStateData.social.friendData)),
        };
    }

    private mergeFriendData(playerEndpointData: IPlayerData): IFriendData[] {
        const friendData: IFriendData[] = [];
        const friendCurrencyRequests: string[] = restoreStringifiedArray(playerEndpointData.friendCurrencyRequests, []);
        const friendsSentCurrency: string[] = restoreStringifiedArray(playerEndpointData.friendsSentCurrency, []);
        const friendGifts: string[] = restoreStringifiedArray(playerEndpointData.friendGifts, []);
        const friendAltGifts: string[] = restoreStringifiedArray(playerEndpointData.friendAltGifts, []);
        const friendHelper: string[] = restoreStringifiedArray(playerEndpointData.friendHelper, []);

        for (let i = 0, n = friendCurrencyRequests.length; i < n; i++) {
            const split = friendCurrencyRequests[i].split('%');
            const friendPlayerId = split[0];
            const currencyRequestDateString = split[1];
            const friend = this.getFriend(friendPlayerId, friendData);
            friend.currencyRequestTimer = restoreDate(currencyRequestDateString, true)?.getTime();
        }
        for (let i = 0, n = friendsSentCurrency.length; i < n; i++) {
            const split = friendsSentCurrency[i].split('%');
            const friendPlayerId = split[0];
            const currencySendDateString = split[1];
            const friend = this.getFriend(friendPlayerId, friendData);
            friend.currencySendTimer = restoreDate(currencySendDateString, true)?.getTime();
        }
        for (let i = 0, n = friendGifts.length; i < n; i++) {
            const split: string[] = friendGifts[i].split('%');
            const friendPlayerId: string = split[0];
            const giftSendDate: string = split[1];
            const friend = this.getFriend(friendPlayerId, friendData);
            friend.giftCooldown = restoreDate(giftSendDate, true)?.getTime();
        }
        for (let i = 0, n = friendAltGifts.length; i < n; i++) {
            const split: string[] = friendAltGifts[i].split('%');
            const friendPlayerId: string = split[0];
            const giftSendDate: string = split[1];
            const friend: IFriendData = this.getFriend(friendPlayerId, friendData);
            friend.altGiftCooldown = restoreDate(giftSendDate, true)?.getTime();
        }
        for (let i = 0, n = friendHelper.length; i < n; i++) {
            const split: string[] = friendHelper[i].split('%');
            const friendPlayerId: string = split[0];
            const helperDate: string = split[1];
            const friend = this.getFriend(friendPlayerId, friendData);
            friend.helperTimer = restoreDate(helperDate, true)?.getTime();
        }

        return friendData;
    }
    private getFriend(friendPlayerId: string, friendData: IFriendData[]): IFriendData {
        // Look for friend data already stored.
        let friend: IFriendData = friendData.find(friend => friend.playerId === friendPlayerId);

        // If friend not found add a new entry.
        if (!friend) {
            // Friend properties must be initialized with nulls for null-checking elsewhere.
            friend = {
                playerId: friendPlayerId,
                currencySendTimer: null,
                currencyRequestTimer: null,
                giftCooldown: null,
                altGiftCooldown: null,
                helperTimer: null,
            };
            friendData.push(friend);
        }

        return friend;
    }
    private getGiftArray(friendData: IFriendData[]): string[] {
        const gift_mas: string[] = [];
        friendData.forEach(value => {
            if (value.giftCooldown) {
                `${value.playerId}%${restoreDate(value.giftCooldown).getTime()}`;
            }
        });

        return gift_mas;
    }
    private getAltGiftsArray(friendData: IFriendData[]): string[] {
        const gift_mas: string[] = [];
        friendData.forEach(value => {
            if (value.altGiftCooldown) {
                gift_mas.push(`${value.playerId}%${restoreDate(value.altGiftCooldown).getTime()}`);
            }
        });

        return gift_mas;
    }
    private getFriendRequestArray(friendData: IFriendData[]): string[] {
        const friendCurrencyRequests: string[] = [];
        friendData.forEach(friend => {
            if (friend.currencyRequestTimer) {
                friendCurrencyRequests.push(`${friend.playerId}%${restoreDate(friend.currencyRequestTimer).getTime()}`);
            }
        });

        return friendCurrencyRequests;
    }
    private getFriendSendArray(friendData: IFriendData[]): string[] {
        const friendsCurrencySent: string[] = [];
        friendData.forEach(friend => {
            if (friend.currencySendTimer) {
                friendsCurrencySent.push(`${friend.playerId}%${restoreDate(friend.currencySendTimer).getTime()}`);
            }
        });

        return friendsCurrencySent;
    }
    private getFriendHelperArray(friendData: IFriendData[]): string[] {
        const friendsHelper: string[] = [];
        friendData.forEach(friend => {
            if (friend.helperTimer) {
                friendsHelper.push(`${friend.playerId}%${restoreDate(friend.helperTimer).getTime()}`);
            }
        });

        return friendsHelper;
    }
}
